import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingsAPI, giftCardsAPI, creditsAPI, exportsAPI } from '@/lib/api';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarDays, Gift, Clock, Loader2, XCircle, Coins, Pencil, CalendarPlus, FileCheck, Users, Camera, Download, Plus, Shield } from 'lucide-react';

const statusColors = {
  confirmed: 'bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/20',
  pending: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
  pending_onsite: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  cancelled: 'bg-red-500/15 text-red-400 border-red-500/20',
};
const statusLabels = { confirmed: 'Confirmée', pending: 'En attente', pending_onsite: 'Paiement sur place', cancelled: 'Annulée' };

function buildCalendarUrl(booking) {
  try {
    const dt = new Date(`${booking.date}T${booking.time_slot}:00`);
    const end = new Date(dt.getTime() + (booking.duration_minutes || 30) * 60000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const title = encodeURIComponent(`${booking.activity_name} - Riding Watt`);
    const loc = encodeURIComponent(booking.site_address || '');
    const details = encodeURIComponent(`Session de ${booking.activity_name} chez ${booking.site_name || 'Riding Watt'}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(dt)}/${fmt(end)}&location=${loc}&details=${details}`;
  } catch { return ''; }
}

function canModify(booking) {
  if (booking.status === 'cancelled') return false;
  try {
    const dt = new Date(`${booking.date}T${booking.time_slot}:00`);
    return (dt - new Date()) > 24 * 3600 * 1000;
  } catch { return false; }
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [creditBalances, setCreditBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modifyModal, setModifyModal] = useState(null);
  const [modifyDate, setModifyDate] = useState('');
  const [modifySlot, setModifySlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [bRes, gcRes, crRes] = await Promise.all([bookingsAPI.getMy(), giftCardsAPI.getMy(), creditsAPI.getBalance()]);
        setBookings(bRes.data);
        setGiftCards(gcRes.data);
        setCreditBalances(crRes.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const cancelBooking = async (id) => {
    try {
      const res = await bookingsAPI.cancel(id);
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status: 'cancelled' } : b));
      const credits = res.data?.credits_added;
      if (credits > 0) {
        toast.success(`Réservation annulée — ${credits}€ de crédits ajoutés à votre compte`);
      } else {
        toast.success('Réservation annulée');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const openModifyModal = (booking) => {
    setModifyModal(booking);
    setModifyDate(booking.date);
    setModifySlot('');
    setAvailableSlots([]);
  };

  const loadSlots = async (date) => {
    if (!modifyModal || !date) return;
    setLoadingSlots(true);
    setModifySlot('');
    try {
      const res = await bookingsAPI.getAvailableSlots(modifyModal.activity_id, date, modifyModal.duration_minutes || 30);
      setAvailableSlots(res.data.slots || []);
    } catch { setAvailableSlots([]); }
    setLoadingSlots(false);
  };

  useEffect(() => {
    if (modifyModal && modifyDate) loadSlots(modifyDate);
  }, [modifyDate]);

  const handleModify = async () => {
    if (!modifySlot) { toast.error('Choisissez un créneau'); return; }
    setSaving(true);
    try {
      await bookingsAPI.modify(modifyModal.id, { date: modifyDate, time_slot: modifySlot });
      setBookings(prev => prev.map(b => b.id === modifyModal.id ? { ...b, date: modifyDate, time_slot: modifySlot } : b));
      toast.success('Réservation modifiée');
      setModifyModal(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
    </div>
  );

  return (
    <>
    <div className="min-h-screen bg-[#0A0A0A] py-8" data-testid="dashboard-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white" data-testid="dashboard-title">
              Bonjour, {user?.name}
            </h1>
            <p className="text-slate-500 mt-2">Gérez vos réservations et cartes cadeaux</p>
          </div>
          <Button
            onClick={async () => {
              try {
                const res = await exportsAPI.exportMyData();
                const url = URL.createObjectURL(res.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = `mes_donnees_riding_watt_${new Date().toISOString().slice(0, 10)}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                toast.success('Vos données ont été téléchargées');
              } catch { toast.error("Erreur lors de l'export"); /* noop */ }
            }}
            variant="outline"
            className="border-[#00FF00]/30 text-[#00FF00] hover:bg-[#00FF00]/10 text-xs"
            data-testid="export-my-data-btn"
            title="Télécharger toutes mes données (RGPD)"
          >
            <Shield className="w-3.5 h-3.5 mr-2" /> Mes données (RGPD)
          </Button>
        </div>

        {creditBalances.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {creditBalances.map(b => (
              <div key={`${b.user_id}-${b.site_id}`}
                className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-[#00FF00]/20 to-[#00FF00]/5 border border-[#00FF00]/30 shadow-[0_0_20px_rgba(0,255,0,0.1)] cursor-pointer hover:border-[#00FF00]/50 transition-all"
                onClick={() => navigate('/credits')}
                data-testid={`dashboard-credits-${b.site_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 flex items-center gap-1"><Coins className="w-3 h-3" /> Crédits</p>
                    <p className="font-heading text-2xl font-bold text-[#00FF00]">{b.balance}€</p>
                  </div>
                  <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/30 text-xs">{b.site_id.replace('site-', '').toUpperCase()}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="mb-8 bg-[#1A1A1A] border border-[#2A2A2A]">
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#00FF00] data-[state=active]:text-black" data-testid="tab-bookings">
              <CalendarDays className="w-4 h-4 mr-2" />
              Réservations ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="giftcards" className="data-[state=active]:bg-[#00FF00] data-[state=active]:text-black" data-testid="tab-giftcards">
              <Gift className="w-4 h-4 mr-2" />
              Cartes Cadeaux ({giftCards.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-slate-400">{bookings.length} réservation{bookings.length > 1 ? 's' : ''}</p>
              <Button
                onClick={() => navigate('/')}
                className="btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold gap-2"
                data-testid="new-booking-btn"
              >
                <Plus className="w-4 h-4" /> Réserver une activité
              </Button>
            </div>
            {bookings.length === 0 ? (
              <div className="text-center py-16">
                <CalendarDays className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                <p className="text-slate-500 mb-4">Aucune réservation</p>
                <Button onClick={() => navigate('/')} className="btn-pill bg-[#00FF00] text-black font-bold" data-testid="browse-activities-btn">
                  Découvrir nos activités
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => {
                  const modifiable = canModify(booking);
                  const calUrl = buildCalendarUrl(booking);
                  return (
                    <div key={booking.id}
                      className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6 hover:border-[#333] transition-colors"
                      data-testid={`booking-card-${booking.id}`}>
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-heading text-lg font-bold text-white">{booking.activity_name}</h3>
                            <Badge className={`${statusColors[booking.status]} border text-xs`}>
                              {statusLabels[booking.status]}
                            </Badge>
                            {booking.status !== 'cancelled' && !booking.participants_registered && (
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/booking/${booking.group_id || booking.id}/participants`); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#FF6600] text-white border border-[#FF6600] animate-pulse-orange cursor-pointer hover:bg-[#E55A00] transition-colors"
                                data-testid={`register-participants-${booking.id}`}
                              >
                                <Users className="w-3 h-3" />
                                Enregistrer les participants
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-3.5 h-3.5" /> {booking.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" /> {booking.time_slot} ({booking.duration_minutes}min)
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading text-xl font-bold text-[#00FF00]">{booking.price}€</span>
                          {booking.status !== 'cancelled' && calUrl && (
                            <a href={calUrl} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/25 hover:bg-blue-500/25 transition-colors"
                              data-testid={`calendar-btn-${booking.id}`}>
                              <CalendarPlus className="w-3.5 h-3.5" /> Agenda
                            </a>
                          )}
                          {booking.status !== 'cancelled' && (
                            <Button variant="ghost" size="sm" onClick={() => {
                              if (booking.contract_signed) {
                                const token = localStorage.getItem('rw_token');
                                const bid = booking.group_id || booking.id;
                                const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/bookings/${bid}/contract/pdf?token=${token}`;
                                window.open(pdfUrl, '_blank');
                              } else {
                                navigate(`/contract/${booking.id}`);
                              }
                            }}
                              className={`${booking.contract_signed ? 'text-[#00FF00] hover:text-[#00CC00]' : 'text-slate-400 hover:text-white'} hover:bg-white/5`}
                              data-testid={`contract-btn-${booking.id}`}>
                              {booking.contract_signed ? (
                                <><Download className="w-4 h-4 mr-1" /> Contrat PDF</>
                              ) : (
                                <><FileCheck className="w-4 h-4 mr-1" /> Contrat</>
                              )}
                            </Button>
                          )}
                          {booking.status !== 'cancelled' && !booking.participants_registered && (
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/booking/${booking.group_id || booking.id}/participants`)}
                              className="text-[#FF6600] hover:text-orange-400 hover:bg-orange-500/10"
                              data-testid={`participants-btn-${booking.id}`}>
                              <Users className="w-4 h-4 mr-1" /> Participants
                            </Button>
                          )}
                          {booking.photo_count > 0 && (
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/booking/${booking.group_id || booking.id}/photos`)}
                              className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                              data-testid={`photos-btn-${booking.id}`}>
                              <Camera className="w-4 h-4 mr-1" /> Photos ({booking.photo_count})
                            </Button>
                          )}
                          {modifiable && (
                            <Button variant="ghost" size="sm" onClick={() => openModifyModal(booking)}
                              className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                              data-testid={`modify-booking-${booking.id}`}>
                              <Pencil className="w-4 h-4 mr-1" /> Modifier
                            </Button>
                          )}
                          {booking.status !== 'cancelled' && (
                            <Button variant="ghost" size="sm" onClick={() => cancelBooking(booking.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`cancel-booking-${booking.id}`}>
                              <XCircle className="w-4 h-4 mr-1" /> Annuler
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="giftcards">
            {giftCards.length === 0 ? (
              <div className="text-center py-16">
                <Gift className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                <p className="text-slate-500 mb-4">Aucune carte cadeau</p>
                <Button onClick={() => navigate('/gift-cards')} className="btn-pill bg-[#00FF00] text-black font-bold" data-testid="buy-gift-card-btn">
                  Acheter une carte cadeau
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {giftCards.map((gc) => (
                  <div key={gc.id}
                    className="relative overflow-hidden rounded-2xl p-6 bg-[#00FF00] shadow-[0_0_30px_rgba(0,255,0,0.2)]"
                    data-testid={`gift-card-${gc.id}`}>
                    <div className="flex justify-between items-start mb-6">
                      <span className="font-heading text-sm font-bold text-black">RIDINGWATT</span>
                      <span className="text-xs font-bold text-black/50 uppercase">Carte Cadeau</span>
                    </div>
                    <p className="text-xs text-black/60 font-mono mb-1">{gc.code}</p>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs text-black/50">Solde restant</p>
                        <p className="font-heading text-3xl font-bold text-black">{gc.balance}€</p>
                      </div>
                      <p className="text-xs text-black/50">/ {gc.initial_amount}€</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modify Booking Modal */}
      <Dialog open={!!modifyModal} onOpenChange={(open) => { if (!open) setModifyModal(null); }}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Modifier la réservation</DialogTitle>
          </DialogHeader>
          {modifyModal && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-slate-400">
                <span className="text-white font-medium">{modifyModal.activity_name}</span> — {modifyModal.duration_minutes} min
              </div>
              <div className="text-xs text-slate-500">
                Actuel : {modifyModal.date} à {modifyModal.time_slot}
              </div>
              <div>
                <Label className="text-xs text-slate-400">Nouvelle date</Label>
                <Input type="date" value={modifyDate}
                  min={new Date(Date.now() + 24 * 3600 * 1000).toISOString().split('T')[0]}
                  onChange={e => setModifyDate(e.target.value)}
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1"
                  data-testid="modify-date-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Nouveau créneau</Label>
                {loadingSlots ? (
                  <div className="flex items-center gap-2 py-4 text-slate-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
                  </div>
                ) : availableSlots.length === 0 ? (
                  <p className="text-xs text-slate-500 py-4">Aucun créneau disponible pour cette date</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2 mt-2 max-h-48 overflow-y-auto">
                    {availableSlots.map(slot => (
                      <button key={slot.time} onClick={() => setModifySlot(slot.time)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                          modifySlot === slot.time
                            ? 'bg-[#00FF00] text-black font-bold'
                            : 'bg-[#0A0A0A] text-slate-300 border border-[#2A2A2A] hover:border-[#00FF00]/40'
                        }`}
                        data-testid={`modify-slot-${slot.time}`}>
                        {slot.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={handleModify} disabled={saving || !modifySlot}
                  className="flex-1 bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold"
                  data-testid="modify-confirm-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}
                  Confirmer
                </Button>
                <Button onClick={() => setModifyModal(null)} variant="outline"
                  className="border-[#2A2A2A] text-slate-400 hover:bg-[#2A2A2A]"
                  data-testid="modify-cancel-btn">
                  Annuler
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    <style>{`
      @keyframes pulse-orange {
        0%, 100% { box-shadow: 0 0 0 0 rgba(255,102,0,0.5); }
        50% { box-shadow: 0 0 12px 3px rgba(255,102,0,0.3); }
      }
      .animate-pulse-orange { animation: pulse-orange 2s ease-in-out infinite; }
    `}</style>
    </>
  );
}
